import React from 'react';
import UIModal from '../../common/ui-components/surface/UIModal';
import Button from '../../common/ui-components/input/Button';
import selfServeModalStyles from '../../../styles/modules/components/SelfServe/selfserve-modal.module.scss';
import commonActionStyles from '../../../styles/modules/components/SelfServe/phone-input-step.module.scss';
import styles from '../../../styles/modules/components/SelfServe/service-request-confirmation-modal.module.scss';

const VARIANTS = {
  AWAITING_CONFIRMATION: 'AWAITING_CONFIRMATION',
  REINITIATE_CONFIRMATION: 'REINITIATE_CONFIRMATION',
};

const DEFAULT_CONTENT = {
  [VARIANTS.AWAITING_CONFIRMATION]: {
    title: 'Almost there!',
    message:
      'Your Service Request for <device> is ready to proceed.\u200bPlease confirm to start your service.',
    note:
      'For your protection, we verify requests before starting them. This helps keep your data secure and ensures your plan benefits are used only by you.',
    primaryButtonLabel: 'Confirm',
    secondaryButtonLabel: 'Not Raised by Me',
  },
  [VARIANTS.REINITIATE_CONFIRMATION]: {
    title: 'Wait a minute...',
    message:
      'Your earlier request didn’t complete due to failed verification. Do you want to continue with the same request?',
    primaryButtonLabel: 'Yes',
    secondaryButtonLabel: 'New SR',
  },
};

interface InitiateSrConfirmationProps {
  isOpen: boolean;
  serviceRequestRefs?: string[];
  serviceRequestLabel?: string;
  onClose: () => void;
  onCancel?: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  secondaryButtonLoading?: boolean;
  isReinitiate?: boolean;
  actionResult?: 'confirmed' | 'cancelled' | null;
  errorMessage?: string;
  secondaryButtonLabel?: string;
  hideCloseButton?: boolean;
}

const InitiateSrConfirmationModal = (props: InitiateSrConfirmationProps) => {
  const [showCancelConfirmation, setShowCancelConfirmation] = React.useState(false);
  const {
    isOpen,
    serviceRequestRefs,
    serviceRequestLabel,
    onClose,
    onCancel,
    onConfirm,
    isLoading,
    secondaryButtonLoading,
    isReinitiate,
    actionResult,
    errorMessage,
    secondaryButtonLabel,
    hideCloseButton,
  } = props;
  const isActionComplete = actionResult === 'confirmed' || actionResult === 'cancelled';
  const variant = isReinitiate
    ? VARIANTS.REINITIATE_CONFIRMATION
    : VARIANTS.AWAITING_CONFIRMATION;
  const defaults =
    DEFAULT_CONTENT[variant] || DEFAULT_CONTENT[VARIANTS.AWAITING_CONFIRMATION];
  const resolvedSecondaryButtonLabel =
    secondaryButtonLabel || defaults.secondaryButtonLabel;
  const isNewSrSecondaryAction = resolvedSecondaryButtonLabel === 'New SR';
  const isPrimaryLoading = Boolean(isLoading);
  const isSecondaryLoading = Boolean(secondaryButtonLoading);
  const isAnyActionLoading = isPrimaryLoading || isSecondaryLoading;
  const primaryButtonLoading = showCancelConfirmation ? isSecondaryLoading : isPrimaryLoading;
  const secondaryButtonCurrentLoading = showCancelConfirmation ? false : isSecondaryLoading;
  const resolvedServiceRequestRefs = Array.isArray(serviceRequestRefs)
    ? serviceRequestRefs
      .map((refNo) => String(refNo || '').trim())
      .filter((refNo) => Boolean(refNo))
    : [];
  const requestCount = resolvedServiceRequestRefs.length;
  const srReferenceText = resolvedServiceRequestRefs.join(', ');
  const requestLabel =
    serviceRequestLabel ||
    srReferenceText ||
    '<device>';
  const requestSubject = requestCount > 1 ? 'Your Service Requests' : 'Your Service Request';
  const requestReadyVerb = requestCount > 1 ? 'are' : 'is';
  const requestCompletedVerb = requestCount > 1 ? 'have' : 'has';
  const requestProcessingVerb = requestCount > 1 ? 'are' : 'is';
  const requestServiceLabel = requestCount > 1 ? 'your services' : 'your service';
  const cancelConfirmationTitle = 'Are you sure?';
  const cancelConfirmationMessage = requestCount > 1
    ? `Are you sure you want to cancel the SRs ${srReferenceText} raised on your ${serviceRequestLabel || 'device'}?`
    : `Are you sure you want to cancel the SR ${srReferenceText || 'request'} raised on your ${serviceRequestLabel || 'device'}?`;

  React.useEffect(() => {
    if (!isOpen || isActionComplete || variant === VARIANTS.REINITIATE_CONFIRMATION) {
      setShowCancelConfirmation(false);
    }
  }, [isActionComplete, isOpen, variant]);

  const getMessage = () => {
    if (actionResult === 'cancelled' && !isNewSrSecondaryAction) {
      return (
        <>
          {`${requestSubject} for ${requestLabel} ${requestCompletedVerb} been cancelled.`}<br />
          You can raise a new request anytime if you still need assistance.
        </>
      );
    }

    if (showCancelConfirmation) {
      return cancelConfirmationMessage;
    }

    if (actionResult === 'confirmed' && variant === VARIANTS.REINITIATE_CONFIRMATION) {
      return `${requestSubject} for ${requestLabel} ${requestCompletedVerb} been reactivated. We are now proceeding with ${requestServiceLabel}.`;
    }

    if (variant === VARIANTS.REINITIATE_CONFIRMATION) {
      return defaults.message.replace('<device>', requestLabel);
    }

    if (actionResult === 'confirmed' && requestCount > 0) {
      return (
        <>
          Confirmation successful!<br />
          {`${requestSubject} for ${requestLabel} ${requestCompletedVerb} been received and ${requestProcessingVerb} now being processed.`}
        </>
      );
    }

    if (requestCount > 0) {
      return `${requestSubject} for ${requestLabel} ${requestReadyVerb} ready to proceed.\u200bPlease confirm to start ${requestServiceLabel}.`;
    }

    return defaults.message;
  };

  const getNoteMessage = () => {
    if (showCancelConfirmation) {
      return '';
    }
    if (variant === VARIANTS.REINITIATE_CONFIRMATION) {
      return '';
    }
    if (typeof defaults.note === 'string') {
      return defaults.note;
    }
    return '';
  };

  const handleSecondaryAction = () => {
    if (isActionComplete) {
      return;
    }
    if (showCancelConfirmation) {
      setShowCancelConfirmation(false);
      return;
    }
    if (variant === VARIANTS.AWAITING_CONFIRMATION) {
      setShowCancelConfirmation(true);
      return;
    }
    onCancel?.();
  };

  const handlePrimaryAction = () => {
    if (showCancelConfirmation) {
      onCancel?.();
      return;
    }
    onConfirm();
  };

  if (!isOpen) {
    return null;
  }
  const shouldShowCloseButton = !hideCloseButton;

  return (
    <UIModal
      isOpen={isOpen}
      onClick={shouldShowCloseButton ? onClose : undefined}
      onOverlayClick={undefined}
      boxClass={styles.modalBox}
      bodyClass={styles.modalBody}
      isNonDismissable={true}
    >
      <div className={`${selfServeModalStyles.selfserve_alert} ${styles.content}`}>
        {shouldShowCloseButton && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close confirmation modal"
          >
            &times;
          </button>
        )}
        <h5 className={`${selfServeModalStyles.selfserve_alert__title} ${styles.title}`}>
          {showCancelConfirmation ? cancelConfirmationTitle : defaults.title}
        </h5>
        <p className={`${selfServeModalStyles.selfserve_alert__text} ${styles.message}`}>
          {getMessage()}
        </p>
        {!isActionComplete && (
          <div className={`${commonActionStyles.button_actions_sticky} ${styles.actions}`} data-varient="non-sticky">
            <Button
              text={showCancelConfirmation ? 'No' : resolvedSecondaryButtonLabel}
              onClick={handleSecondaryAction}
              variant="secondary"
              className={`${commonActionStyles.cart_btn} ${styles.button} ${styles.secondaryButton}`}
              loading={secondaryButtonCurrentLoading}
              disabled={isAnyActionLoading || isActionComplete}
            />
            <Button
              text={showCancelConfirmation ? 'Yes' : defaults.primaryButtonLabel}
              onClick={handlePrimaryAction}
              className={`${commonActionStyles.cart_btn} ${styles.button} ${styles.primaryButton}`}
              loading={primaryButtonLoading}
              disabled={isAnyActionLoading || isActionComplete}
            />
          </div>
        )}
        {Boolean(getNoteMessage()) && !isActionComplete && (
          <p className={`${styles.note}`}>{getNoteMessage()}</p>
        )}
        {Boolean(errorMessage) && !isActionComplete && (
          <p className={styles.errorText}>{errorMessage}</p>
        )}
      </div>
    </UIModal>
  );
};

export default InitiateSrConfirmationModal;
